import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { getTopic } from "@/lib/api";
import BrandBand from "@/components/BrandBand";
import Seo, { faqPageLd, breadcrumbLd } from "@/components/Seo";

export default function Topic() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [openFaqs, setOpenFaqs] = useState({});

  useEffect(() => {
    setTopic(null);
    getTopic(slug).then(setTopic).catch(() => setTopic({ notFound: true }));
    window.scrollTo(0, 0);
  }, [slug]);

  if (!topic) return <div className="section max-w-[900px] mx-auto px-6 opacity-60">Loading…</div>;
  if (topic.notFound) return <div className="section max-w-[900px] mx-auto px-6 font-serif-editorial text-3xl">Topic not found.</div>;

  const ld = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: topic.title, path: `/topic/${slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: `${topic.title} — SIBO Institute`,
      description: topic.lede,
      about: { "@type": "MedicalCondition", name: "Small Intestinal Bacterial Overgrowth" },
      audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    },
  ];
  if (topic.faqs && topic.faqs.length) ld.push(faqPageLd(topic.faqs));

  return (
    <div data-testid="topic-page">
      <Seo
        title={topic.title}
        description={topic.lede}
        path={`/topic/${slug}`}
        image={topic.image}
        jsonLd={ld}
      />

      <section className="section pb-0">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="overline">{topic.kicker}</div>
            <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(3rem, 7vw, 6rem)", lineHeight: 0.95 }} data-testid="topic-title">
              {topic.title}
            </h1>
            <p className="mt-10 text-xl font-serif-editorial" style={{ maxWidth: "34rem", lineHeight: 1.4 }}>
              {topic.lede}
            </p>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <img src={topic.image} alt={topic.title} className="w-full aspect-[4/5] object-cover" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8 md:col-start-3">
            {topic.sections?.map((sec, i) => (
              <div key={i} className="mb-16" data-testid={`topic-section-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                  <div className="md:col-span-1">
                    <div className="overline">{String(i + 1).padStart(2, "0")}</div>
                    <h2 className="font-serif-editorial mt-3" style={{ fontSize: "1.9rem", lineHeight: 1.1 }}>{sec.h}</h2>
                  </div>
                  <ul className="md:col-span-3 space-y-3">
                    {sec.items.map((it, j) => (
                      <li key={j} className="text-lg border-b pb-3" style={{ borderColor: "rgba(166,154,139,0.4)" }}>
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {topic.faqs && topic.faqs.length > 0 && (
        <section className="section pt-0" data-testid="topic-faqs">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8 md:col-start-3">
              <div className="overline">Frequently asked</div>
              <h2 className="font-serif-editorial tight-track mt-4 mb-10" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1.05 }}>
                Questions about {topic.title.toLowerCase()}.
              </h2>
              <div>
                {topic.faqs.map((f) => {
                  const isOpen = !!openFaqs[f.id];
                  return (
                    <div key={f.id} className="border-b" style={{ borderColor: "rgba(166,154,139,0.4)" }}>
                      <button
                        onClick={() => setOpenFaqs((o) => ({ ...o, [f.id]: !o[f.id] }))}
                        data-testid={`topic-faq-toggle-${f.id}`}
                        className="w-full py-6 flex items-start justify-between gap-6 text-left"
                        style={{ color: "var(--olive)" }}
                      >
                        <span className="font-serif-editorial" style={{ fontSize: "1.4rem", lineHeight: 1.25, flex: 1 }}>
                          {f.question}
                        </span>
                        <ChevronDown size={20} style={{ transition: "transform 250ms ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0, marginTop: "4px" }} />
                      </button>
                      {isOpen && (
                        <div className="pb-8 pr-8 md:pr-16" style={{ maxWidth: "48rem" }}>
                          <div className="text-base leading-relaxed">{f.answer}</div>
                          {f.references && f.references.length > 0 && (
                            <div className="mt-6 pt-5 border-t" style={{ borderColor: "rgba(166,154,139,0.35)" }}>
                              <div className="overline mb-3">References</div>
                              <ul className="space-y-1.5">
                                {f.references.map((r, ri) => (
                                  <li key={ri} className="text-sm opacity-80" style={{ lineHeight: 1.5 }}>— {r}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-10">
                <Link to="/faqs" className="btn-outline" data-testid="view-all-faqs">All FAQs</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {topic.articles && topic.articles.length > 0 && (
        <section className="section pt-0" style={{ backgroundColor: "var(--sand)" }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24">
            <div className="overline">Related reading</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-12">
              {topic.articles.map((a) => (
                <Link to={`/article/${a.slug}`} key={a.slug} className="group" data-testid={`topic-related-${a.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="overline mt-6">{a.reading_time}</div>
                  <div className="font-serif-editorial mt-3" style={{ fontSize: "1.5rem", lineHeight: 1.2 }}>{a.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BrandBand />
    </div>
  );
}
